import React from 'react'
import { useIntl } from 'react-intl'
import { CircularProgress, TableHead } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import { capitalize } from '../../../../utils/functions'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import {
    CourseState,
    GroupSubjectState,
    TeacherState,
} from '../../../../features/types'
import AddGroupSubjectDialog from './AddGroupSubjectDialog'

interface Props {
    data: GroupSubjectState[]
    teachers: TeacherState[]
    course: CourseState
    isFetching: boolean
    isFetchingCourse: boolean
    isFetchingTeachers: boolean
    onDelete?: (subject: number) => void
}

const GroupSubjectsTable: React.FC<Props> = ({
    data,
    teachers,
    course,
    isFetching,
    isFetchingTeachers,
    onDelete,
}) => {
    const { formatMessage, locale } = useIntl()
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(5)

    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const getTeacherOfGroup = (teacherId) => {
        const teacher = teachers?.find((teacher) => teacher.id === teacherId)

        if (teacher) {
            return teacher.firstName + ' ' + teacher.lastName
        }
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'subject' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'teacher' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'link' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'comment' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'course' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'edit' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'delete' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {data.length ? (
                        <TableBody>
                            {(rowsPerPage > 0
                                ? data.slice(
                                      page * rowsPerPage,
                                      page * rowsPerPage + rowsPerPage,
                                  )
                                : data
                            ).map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {
                                            row[
                                                `subjectTitle${capitalize(
                                                    locale,
                                                )}`
                                            ]
                                        }
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {isFetchingTeachers ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            getTeacherOfGroup(row.teacherId)
                                        )}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.lessonLink}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.lessonComment}
                                    </TableCell>

                                    <TableCell>
                                        {
                                            row[
                                                `courseTitle${capitalize(
                                                    locale,
                                                )}`
                                            ]
                                        }
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <AddGroupSubjectDialog
                                            editMode
                                            editSubject={row}
                                            courseId={course?.id}
                                        />
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <ConfirmDelete
                                            confirmAction={() =>
                                                onDelete(row.id as number)
                                            }
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}

                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    ) : (
                        <TableBody>
                            <TableRow>
                                {!isFetching && (
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={7}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableBody>
                    )}

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={8}
                                count={data?.length || 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default GroupSubjectsTable
